import { Box, Button, Divider, HStack, IconButton, Spinner, Text, useDisclosure, VStack } from "@chakra-ui/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { getChat, getMyProfile } from "../../../api";
import Template from "../../../components/Layout/Template";
import { colors } from "../../../styles";
import { IChatDetail, IMyProfile } from "../../../types";
import Comment from "../../../components/Chat/Comment";
import MyIcon from "../../../components/Icon/MyIcon";
import { AddIcon } from "@chakra-ui/icons";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import FakeInput from "../../../components/Chat/FakeInput";
import CommentOnModal from "./CommentOnModal";
import FilterModal, { CommentSortFilter } from "../FilterModal";
import DetailChatItem from "../../../components/Chat/ListItem/DetailChatItem";
import LoadingRipple from "../../../components/Indicator/Ripple";
import { ChatDeleteLoadingProvider } from "../../../components/Chat/ChatDeleteLoading";
import { INTENTIONAL_LOADING_MS } from "../../../hooks/useMinDelayLoading";

export default function ChatDetail() {
    
    /* params */

    const { slug } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    /* query: myProfileData */

    const { data: myProfileData } = useQuery<IMyProfile>({
        queryKey: ["getMyProfile"],
        queryFn: () => getMyProfile(),
    });

    /* state */

    const [filter, setFilter] = useState<CommentSortFilter>("latest");

    /* query: chatData */

    const { data: chatData, isLoading: isChatDataLoading } = useQuery<IChatDetail>({
        queryKey: ["chat", slug],
        queryFn: () => getChat({ slug: slug! }),
        enabled: !!slug,
    });


    const { isOpen: isAddCommentModalOpen, onOpen: onAddCommentModalOpen, onClose: onAddCommentModalClose } = useDisclosure();
    const { isOpen: isFilterModalOpen, onOpen: onFilterModalOpen, onClose: onFilterModalClose } = useDisclosure();

    const filterLabel = filter === "latest" ? "최신순" : "인기순";

    /* 게시 로딩 인디케이터
     * 인디케이터 노출 시간 = max(INTENTIONAL_LOADING_MS, 실제 API 응답 시간)
     * - 게시 버튼 클릭 시 isPostLoading = true
     * - 최소 지연 완료 && API 응답 완료, 두 조건이 모두 충족돼야 숨김
     */

    const [isPostLoading, setIsPostLoading] = useState(false);
    const minDelayDoneRef = useRef(false);
    const apiDoneRef = useRef(false);
    const minDelayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const tryHideIndicator = () => {
        if (minDelayDoneRef.current && apiDoneRef.current) {
            queryClient.invalidateQueries({ queryKey: ["chat", slug] });
            queryClient.invalidateQueries({ queryKey: ["chats"] });
            queryClient.invalidateQueries({ queryKey: ["getProfile"] });
            setIsPostLoading(false);
            minDelayDoneRef.current = false;
            apiDoneRef.current = false;
        }
    };

    const handlePostStart = () => {
        minDelayDoneRef.current = false;
        apiDoneRef.current = false;
        setIsPostLoading(true);

        if (minDelayTimerRef.current) clearTimeout(minDelayTimerRef.current);
        minDelayTimerRef.current = setTimeout(() => {
            minDelayDoneRef.current = true;
            tryHideIndicator();
        }, INTENTIONAL_LOADING_MS);
    };

    const handlePostDone = () => {
        apiDoneRef.current = true;
        tryHideIndicator();
    };

    const handlePostError = () => {
        if (minDelayTimerRef.current) clearTimeout(minDelayTimerRef.current);
        setIsPostLoading(false);
        minDelayDoneRef.current = false;
        apiDoneRef.current = false;
    };

    useEffect(() => {
        return () => {
            if (minDelayTimerRef.current) clearTimeout(minDelayTimerRef.current);
        };
    }, []);

    /* 필터 재정렬 로직 */

    const sortedComments = useMemo(() => {
        if (!chatData?.comments) return [];

        const comments = [...chatData.comments];

        if (filter === "latest") {
            return comments.sort(
                (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
            );
        }

        return comments.sort(
            (a, b) =>
               ( b.likeCount + b.replyCount ) - (a.likeCount + a.replyCount)
        );
    }, [chatData?.comments, filter]);

    /* render */

    return (
        <ChatDeleteLoadingProvider
            onComplete={() => {
                queryClient.invalidateQueries({ queryKey: ["chat", slug] });
                queryClient.invalidateQueries({ queryKey: ["chats"] });
                queryClient.invalidateQueries({ queryKey: ["getProfile"] });
            }}
        >
        <Template>
            {isChatDataLoading ?
                <VStack width={"100%"} justifyContent={"center"} alignItems={"center"} paddingY={10}>
                    <Spinner color={colors.blue} />
                </VStack>
                    : chatData ?
                        (chatData.isBlocked || chatData.isBlocking || chatData.isMyTypeOnlyButNotMyType) ? 
                            <VStack 
                                width={"100%"}
                                height={"80vh"}
                                justifyContent={"center"}
                                alignItems={"center"}
                                spacing={4}
                            >
                                <Text fontSize={14} fontWeight={500} color={colors.g3} textAlign={"center"}>
                                    접근 권한이 없습니다.
                                </Text>
                                <Button 
                                    variant={"ghost"}
                                    backgroundColor={colors.light}
                                    fontSize={14}
                                    fontWeight={500}
                                    color={colors.blue}
                                    onClick={() => navigate("/chat")}
                                >
                                    목록으로 돌아가기
                                </Button>
                            </VStack>
                         : 
                        <VStack
                            width={"100%"}
                            justifyContent={"flex-start"}
                            alignItems={"center"}
                            spacing={5}
                        >
                            <DetailChatItem
                                id={chatData.id}
                                slug={chatData.slug}
                                profileId={chatData.profile.id}
                                profileImageUrl={chatData.profile.avatar}
                                userType={chatData.profile.user.type}
                                authorType={chatData.authorType}
                                nickname={chatData.profile.nickname ?? ""}
                                content={chatData.content}
                                imageUrls={chatData.imageUrls}
                                isMyTypeOnly={chatData.isMyTypeOnly}
                                likeCount={chatData.likeCount}
                                commentCount={chatData.commentCount}
                                saveCount={chatData.saveCount}
                                isMine={chatData.isMine}
                                isLiked={chatData.isLiked}
                                isSaved={chatData.isSaved}
                                isReported={chatData.isReported}
                                isBlocking={chatData.isBlocking}
                                hiddenAt={chatData.hiddenAt}
                                createdAt={chatData.createdAt}
                                onDeleteSuccess={() => navigate("/chat")}
                            />

                            <Divider
                                width={{
                                    base:"100%",
                                    sm:"100%",
                                    md:"100%",
                                    lg:"80%",
                                    xl:"80%",
                                }}
                                marginY={4}
                            />

                            <HStack
                                width={{
                                    base:"100%",
                                    sm:"100%",
                                    md:"100%",
                                    lg:"80%",
                                    xl:"80%",
                                }}
                                justifyContent={"space-between"}
                                alignItems={"center"}
                                marginBottom={4}
                            >
                                {chatData.comments.length > 0 ? 
                                    <HStack 
                                        as="button"
                                        type="button"
                                        justifyContent={"flex-end"} 
                                        alignItems={"center"}
                                        gap={2}
                                        onClick={onFilterModalOpen}
                                    >
                                        <MyIcon src={"/svg/filter-2.svg"} alt={"필터"} width={16} height={16} />
                                        <Text fontSize={14} fontWeight={500} color={colors.black}>{filterLabel}</Text>
                                    </HStack>
                                :
                                    <Box />
                                }
                                <HStack 
                                    gap={2} 
                                    justifyContent={"flex-start"} 
                                    alignItems={"center"}
                                >
                                    <Text fontSize={14} fontWeight={500} color={colors.black}>댓글 {chatData.commentCount}개</Text>
                                </HStack>
                            </HStack>

                            {/* FakeInput */}

                            <FakeInput
                                avatar={myProfileData?.avatar ?? "/png/profile.png"}
                                nickname={myProfileData?.nickname ?? ""}
                                description={`${chatData.profile.nickname} 님에게 댓글 남기기...`}
                                onClick={onAddCommentModalOpen}
                            />

                            {/* Comments */}
                            
                            <VStack
                                width={"100%"}
                                justifyContent={"flex-start"}
                                alignItems={"center"}
                                spacing={0}
                            >
                                {sortedComments.length > 0 && (
                                    sortedComments.map((comment, index) => {

                                        const isFirst = index === 0;
                                        const isLast = index === sortedComments.length - 1;

                                        return (
                                            <Fragment key={comment.id}>
                                                <Comment
                                                    slug={chatData.slug}
                                                    comment={comment}
                                                    isFirst={isFirst}
                                                    isLast={isLast}
                                                    onPostStart={handlePostStart}
                                                    onPostDone={handlePostDone}
                                                    onPostError={handlePostError}
                                                />

                                                {!isLast && (
                                                    <Divider
                                                        width={{
                                                            base:"100%",
                                                            sm:"100%",
                                                            md:"100%",
                                                            lg:"80%",
                                                            xl:"80%",
                                                        }}
                                                    />
                                                )}
                                            </Fragment>
                                        );
                                    })
                                )}
                            </VStack>
                            <VStack
                                position={"fixed"}
                                right={{ base: 4, sm: 8, md: 12 }}
                                bottom={{ base: 4, sm: 8, md: 12 }}
                                spacing={{ base: 3, sm: 4, md: 4 }}
                            >
                                <IconButton
                                    aria-label={"채팅 추가"}
                                    icon={<AddIcon boxSize={{ base: 6, sm: 7, md: 8 }} />}
                                    boxSize={{ base: 16, sm: 20, md: 24 }}
                                    minWidth={{ base: 16, sm: 20, md: 24 }}
                                    borderRadius={"full"}
                                    backgroundColor={colors.blue}
                                    color={colors.white}
                                    boxShadow={"lg"}
                                    _hover={{ backgroundColor: colors.blue }}
                                    _active={{ backgroundColor: colors.blue }}
                                    onClick={onAddCommentModalOpen}
                                />
                            </VStack>

                            {/* CommentOnModal */}

                            <CommentOnModal 
                                isOpen={isAddCommentModalOpen} 
                                onClose={onAddCommentModalClose}
                                slug={chatData.slug}
                                onPostStart={handlePostStart}
                                onPostDone={handlePostDone}
                                onPostError={handlePostError}
                                chat={{
                                    id: chatData.id,
                                    profile: {
                                        id: chatData.profile.id,
                                        avatar: chatData.profile.avatar,
                                        nickname: chatData.profile.nickname ?? "",
                                    },
                                    content: chatData.content,
                                }} 
                            />

                            {/* FilterModal */}
                            
                            <FilterModal
                                isOpen={isFilterModalOpen}
                                onClose={onFilterModalClose}
                                filter={filter}
                                onChangeFilter={setFilter}
                            />
                        </VStack>
                : 
                    <VStack 
                        width={"100%"}
                        height={"80vh"}
                        justifyContent={"center"}
                        alignItems={"center"}
                        spacing={4}
                    >
                        <Text fontSize={14} fontWeight={500} color={colors.g3} textAlign={"center"}>
                            챗 내용을 불러오지 못했습니다.
                        </Text>
                        <Button 
                            variant={"ghost"}
                            backgroundColor={colors.light}
                            fontSize={14}
                            fontWeight={500}
                            color={colors.blue}
                            onClick={() => navigate("/")}
                        >
                            홈으로 돌아가기
                        </Button>
                    </VStack>
            }

            {isPostLoading && (
                <Box
                    position="fixed"
                    inset={0}
                    zIndex={9999}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    pointerEvents="none"
                >
                    <LoadingRipple />
                </Box>
            )}
        </Template>
        </ChatDeleteLoadingProvider>
    );
}
